import React, { useState } from "react"
import styled, { keyframes } from "styled-components"
import claraLogo from "../../assets/images/clara-logo.png"

// Clara's working mark: a bud pinches off the Clara orb, circles twice dragging a liquid
// tail, and is pulled back in.
//
// The pieces are white circles in a mask behind a blur and a hard alpha cut, so any two
// that come close melt into one shape. The gradient stays still behind the mask, and the
// real logo rides on the body so its glass highlight is never lost.

let nextId = 0

const TAIL = [7.5, 5.5]

const spin = keyframes`
	to { transform: rotate(720deg); }
`

const bud = keyframes`
	0%, 4%    { transform: translate(0, 0); animation-timing-function: cubic-bezier(0.6, 0, 0.3, 1); }
	26%, 74%  { transform: translate(0, -40px); animation-timing-function: cubic-bezier(0.6, 0, 0.4, 1); }
	94%, 100% { transform: translate(0, 0); }
`

const body = keyframes`
	0%, 4%   { transform: translate(0, 0) scale(1, 1); }
	16%      { transform: translate(0, -4px) scale(0.9, 1.06); }
	30%, 72% { transform: translate(0, 0) scale(0.8, 0.8); }
	88%      { transform: translate(0, -3px) scale(0.92, 1.02); }
	95%      { transform: translate(0, 0) scale(1.05, 0.97); }
	100%     { transform: translate(0, 0) scale(1, 1); }
`

const glass = keyframes`
	0%, 4%   { transform: scale(1); }
	16%      { transform: scale(0.96); }
	30%, 72% { transform: scale(0.8); }
	88%      { transform: scale(0.97); }
	95%      { transform: scale(1.04); }
	100%     { transform: scale(1); }
`

export function ClaraOrb({ size = 22 }) {
	const [id] = useState(() => `clara-orb-${++nextId}`)

	return (
		<Svg width={size} height={size} viewBox="0 0 100 100" aria-hidden="true">
			<defs>
				<linearGradient id={`${id}-g`} x1="50" y1="23" x2="50" y2="77" gradientUnits="userSpaceOnUse">
					<stop offset="0" stopColor="#0C8CE9" />
					<stop offset="0.5" stopColor="#56ABF4" />
					<stop offset="1" stopColor="#F8BCFF" />
				</linearGradient>
				<filter id={`${id}-f`} filterUnits="userSpaceOnUse" x="-10" y="-10" width="120" height="120">
					<feGaussianBlur stdDeviation="5" />
					<feColorMatrix values="1 0 0 0 0  0 1 0 0 0  0 0 1 0 0  0 0 0 24 -10" />
				</filter>
				<mask id={`${id}-m`} maskUnits="userSpaceOnUse" x="0" y="0" width="100" height="100">
					<g filter={`url(#${id}-f)`} fill="#fff">
						<g className="spin">
							<circle className="body" cx="50" cy="50" r="27" />
							<circle className="bud" cx="50" cy="50" r="10" />
						</g>
						{TAIL.map((r, index) => {
							const delay = { animationDelay: `${(index + 1) * 70}ms` }
							return (
								<g key={r} className="spin" style={delay}>
									<circle className="bud" cx="50" cy="50" r={r} style={delay} />
								</g>
							)
						})}
					</g>
				</mask>
			</defs>
			<rect width="100" height="100" fill={`url(#${id}-g)`} mask={`url(#${id}-m)`} />
			<image className="glass" href={claraLogo} x="23" y="23" width="54" height="54" />
		</Svg>
	)
}

const Svg = styled.svg`
	display: block;
	flex-shrink: 0;
	overflow: visible;

	circle {
		transform-box: fill-box;
		transform-origin: center;
	}

	.spin,
	.glass {
		transform-box: view-box;
		transform-origin: 50px 50px;
	}

	.spin {
		animation: ${spin} 4s cubic-bezier(0.4, 0, 0.6, 1) infinite;
	}

	.body {
		animation: ${body} 4s ease-in-out infinite;
	}

	.bud {
		animation: ${bud} 4s infinite;
	}

	.glass {
		animation: ${glass} 4s ease-in-out infinite;
	}

	[data-color-scheme="dark"] & {
		filter: drop-shadow(0 0 3px rgba(86, 171, 244, 0.5));
	}

	/* Held still it is just the Clara logo, which still says she is working beside the
	   label. */
	@media (prefers-reduced-motion: reduce) {
		.spin,
		.body,
		.bud,
		.glass {
			animation: none;
		}
	}
`

export default ClaraOrb
